// Adaptive resolution: maintain a 60-frame rolling mean of frame time (ms).
//
// Cold start: begin at LOW_SCALE so first-paint never tries full-res before
// the AdaptiveScaler has data. Ramp up to 1.0 after RECOVERY_FRAMES of
// healthy frametimes (capable hardware reaches full res within ~1s). If
// sustained slowness pushes the mean back over SLOW_MS, drop to LOW_SCALE.

const WINDOW = 60;
const SLOW_MS = 19;
// 17ms — a vsync-locked 60fps frame is ~16.67ms. A 16ms threshold excluded
// virtually every healthy frame (most arrive at 16.5-16.7ms), so the scaler
// never qualified frames as "healthy" and never ramped back to 1.0. The
// scale would stick at LOW_SCALE forever and the render looked blurry.
const HEALTHY_MS = 17;
const RECOVERY_FRAMES = 60;
const LOW_SCALE = 2 / 3;

export class AdaptiveScaler {
  constructor() {
    this.scale = LOW_SCALE;
    this.history = new Float32Array(WINDOW);
    this.cursor = 0;
    this.filled = 0;
    this.healthyStreak = 0;
    this._lastMs = 0;
  }

  recordFrameMs(ms) {
    this.history[this.cursor] = ms;
    this.cursor = (this.cursor + 1) % WINDOW;
    if (this.filled < WINDOW) this.filled++;
    this._lastMs = ms;
  }

  tick() {
    // Both directions react to the rolling-window MEAN (not individual frames).
    // Using a streak counter for upgrade was too brittle: a single jittery
    // frame (e.g. 17.5ms) would reset the streak even though the GPU was
    // clearly healthy on average — the scaler would stick at LOW_SCALE forever
    // and the render looked blurry. Mean-based logic mirrors the downgrade
    // path: if the average is consistently below HEALTHY_MS, ramp up.
    if (this.filled < WINDOW) return;

    let sum = 0;
    for (let i = 0; i < WINDOW; i++) sum += this.history[i];
    const mean = sum / WINDOW;

    if (this.scale === 1.0 && mean > SLOW_MS) {
      this.scale = LOW_SCALE;
      this.healthyStreak = 0;
      return;
    }

    if (this.scale < 1.0 && mean <= HEALTHY_MS) {
      this.healthyStreak++;
      if (this.healthyStreak >= RECOVERY_FRAMES) {
        this.scale = 1.0;
        this.healthyStreak = 0;
      }
    } else if (this.scale < 1.0) {
      this.healthyStreak = 0;
    }
  }
}
